import React, {useContext, useEffect, useMemo, useState} from "react";
import Header from "../components/header/header";
import SidebarProvider from "../components/sidebar/sidebar";
import styles from '../../styles/main.module.css';
import {
    PrimaryButton,
    SelectInput,
    TextInput
} from "../../components/input/inputs";
import TableComponent from "../../components/table/table";
import Loading from "../../components/loading/loading";
import Title from "../../components/title/title";
import axiosInstance from "../../AxiosInstance";
import {DateInput as DateInputAjonjolib} from "../../components/ajonjolib/inputs/ajonjolinput";
import {evalToString, pagoToString, StatusChip} from "../../utils";
import {parseAmount} from "../../utils/utils";
import MyContext from "../../MyContext";
import Pagination from "../../components/ajonjolib/utils/pagination";
import { openInvoiceDetail } from "../../modals/bus/modalApi";
import AuditHistoryModal from "../../modals/audit_history_modal";
import filterStyles from "../../styles/consultationFilters.module.css";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronUp, faClockRotateLeft} from "@fortawesome/free-solid-svg-icons";
import tableChrome from "../../components/table/consultationTableChrome.module.css";
import { invoiceListHandlersRef } from "../../modals/bus/invoiceListHandlersRef";

export default function InvoicesProvider() {
    const [form, setForm] = useState({});
    const [rows, setRows] = useState([]);
    const [filteredRows, setFilteredRows] = useState([]);
    const [secondaryQuery, setSecondaryQuery] = useState("");
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [filtersCollapsed, setFiltersCollapsed] = useState(false);
    const [userType, setUserType] = useState("");
    const {isClient} = useContext(MyContext);
    const [loaded, setLoaded] = useState(false);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageCount, setPageCount] = useState(1);
    const [maxItemsPerPage] = useState(25);
    const [auditHistory, setAuditHistory] = useState({
        show: false,
        mode: "bill",
        entityId: null,
        titleLabel: "",
    });

    useEffect(() => {
        if(isClient) {
            setUserType("proveedor");
        } else {
            setUserType("cliente");
        }
    }, [isClient]);

    useEffect(() => {
        query(1);
    }, []);

    useEffect(() => {
        invoiceListHandlersRef.current = {
            refreshCurrentPage: () => query(currentPage),
        };
        return () => {
            invoiceListHandlersRef.current = null;
        };
    });

    const query = (page = 1) => {
        setLoaded(false);
        const queryParams = Object.entries(form)
            .filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== '')
            .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
            .join("&");
        const url = `bill/?page=${page}&${queryParams}`;
        axiosInstance.get(url).then((response) => {
            const data = response.data;
            setRows(data.results);
            setFilteredRows(data.results);
            const totalPages = Math.ceil(data.count / maxItemsPerPage);
            setPageCount(totalPages);
            setLoaded(true);
        })
    }

    const cols = useMemo(() => {
        const evalTooltip = (s) => {
            switch (s) {
                case -1:
                    return 'Cargando...';
                case 0:
                    return 'Pendiente';
                case 1:
                    return 'Observado';
                case 2:
                    return 'Aceptado';
                case 3:
                    return 'Rechazado';
                case 4:
                    return 'Pre-aprobado';
                default:
                    return 'Pendiente';
            }
        };
        const pagoTooltip = (s) => (s === 0 ? 'Pendiente' : 'Recibido');

        return [
        {title: "Fecha recepción" , internal: "reception_date"},
        {title: "Fecha emisión" , internal: "issue_date"},
        {title: "Promesa de Pago" , internal: "payment_promise_date"},
        {title: "Tipo Doc.", internal: "invoice_type",
            tooltipText: (row) =>
                row.invoice_type === 0 ? 'Boleta' : row.invoice_type === 1 ? 'Factura' : 'RHE',
            code: (row, data) => {
            if (data === 0) {
                return <StatusChip variant="muted">Boleta</StatusChip>;
            }
            if (data === 1) {
                return <StatusChip variant="info">Factura</StatusChip>;
            }
            return <StatusChip variant="orange">RHE</StatusChip>;
            }},
        {title: "Serie - Número", internal: "sn"},
        {title: userType, internal: "id",
            tooltipText: (row) =>
                userType === "proveedor"
                    ? row?.purchase_order?.provider?.name ?? ''
                    : userType === "cliente"
                      ? row?.purchase_order?.client?.name ?? ''
                      : '',
            code: (row, data) => {
                if(userType === "proveedor") {
                    return row.purchase_order.provider.name;
                } else if(userType === "cliente") {
                    return row.purchase_order.client.name;
                }
            }},
        {title: "Número OC", internal: "purchase_order.reference"},
        {title: "Moneda", internal: "currency.name"},
        {title: "Monto Total", internal: "total",
            tooltipText: (row) => parseAmount(row.total),
            code: (row, data) =>  {return (<div className={'text-end'}>{parseAmount(data)}</div>)}},
        {title: "Estado Evaluación", internal: "eval_status",
            tooltipText: (row) => evalTooltip(row.eval_status),
            code: (row, data) => {
            return <div>{evalToString(data)}</div>;
            }},
        {title: "Estado Pago", internal: "payment_status",
            tooltipText: (row) => pagoTooltip(row.payment_status),
            code: (row, data) => {
            return <div>{pagoToString(data)}</div>;
            }},
        {title: "Acciones", internal: 'type', code: (row, data) => (
            <div className="d-flex align-items-center gap-2 flex-wrap">
                <div
                    onClick={() => {
                        axiosInstance.get(`sunat/${row.id}/`).then((response) => {
                            let bill = { ...row, sunat_status: -1, conformity_status: -1 };
                            if (response?.status === 200 && response?.data) {
                                const response_data = response.data;
                                bill = {
                                    ...row,
                                    sunat_status: response_data.ose_sunat_status,
                                    conformity_status: response_data.conformity_status
                                };
                            }
                            openInvoiceDetail({ bill });
                        }).catch(() => {
                            openInvoiceDetail({
                                bill: { ...row, sunat_status: -2, conformity_status: -2 }
                            });
                        });
                    }}
                    role={'button'} className={tableChrome.detailBtn}>Detalles</div>
                <button
                    type="button"
                    className={tableChrome.historyIconBtn}
                    title="Historial de eventos"
                    aria-label="Historial de eventos"
                    onClick={(e) => {
                        e.stopPropagation();
                        setAuditHistory({
                            show: true,
                            mode: "bill",
                            entityId: row.id,
                            titleLabel: row.sn ?? String(row.id),
                        });
                    }}
                >
                    <FontAwesomeIcon icon={faClockRotateLeft} />
                </button>
            </div>
        )},
        ];
    }, [userType]);

    useEffect(() => {
        if(secondaryQuery === undefined || secondaryQuery === "") setFilteredRows(rows);
        const lowerCaseQuery = secondaryQuery.toLowerCase();
        const filtered = rows.filter((row) => {
            return (row?.sn?.toString()?.toLowerCase().includes(lowerCaseQuery)
                || row?.purchase_order?.client?.name?.toLowerCase().includes(lowerCaseQuery)
                || row?.total?.toString().toLowerCase().includes(lowerCaseQuery)
                || row?.total?.toString().toLowerCase().includes(lowerCaseQuery));
        })
        setFilteredRows(filtered);
    }, [secondaryQuery, rows]);

    return (
        <div className={styles.mainContainer}>
            <Header setSidebarOpen={setSidebarOpen}/>
            <SidebarProvider index={1} isOpen={sidebarOpen}/>

            <div className={styles.container} style={!sidebarOpen ? {marginLeft: '80px'} : {marginLeft: '285px'}}>
                <Title name={'Facturas'} consultation/>

                <div className={[filterStyles.filterShell, filtersCollapsed ? filterStyles.filterShellCollapsed : ''].filter(Boolean).join(' ')}>
                    <div className={filterStyles.filterSectionHead}>
                        <div className={filterStyles.sectionTitle}>Filtros</div>
                        <button
                            type="button"
                            className={filterStyles.filterCollapseToggle}
                            aria-expanded={!filtersCollapsed}
                            aria-label={filtersCollapsed ? 'Mostrar filtros' : 'Ocultar filtros'}
                            onClick={() => setFiltersCollapsed((c) => !c)}
                        >
                            <FontAwesomeIcon
                                icon={faChevronUp}
                                className={[filterStyles.filterChevron, filtersCollapsed ? filterStyles.filterChevronCollapsed : ''].filter(Boolean).join(' ')}
                            />
                        </button>
                    </div>

                    <div className={filterStyles.filterCollapseOuter}>
                        <div className={filterStyles.filterCollapseInner}>
                            <div className={filterStyles.gridWide}>
                                <div className={filterStyles.stack}>
                                    <SelectInput greyBackground={true} name={'invoice_type'} placeholder={'Tipo Documento'} internalName={'invoice_type'} options={[{id: 1, name: 'Factura'}, {id: 2, name: "RHE"}]} form={form} setForm={setForm} hideName={true}/>
                                    <DateInputAjonjolib placeholder={'Fecha de recepción'} onChange={(data) => {
                                        setForm({
                                            ...form,
                                            'reception_date': data.startDate.toLocaleString('es')?.split(',')[0].replace(/\//g, '-'),
                                            'reception_date_end': data.endDate.toLocaleString('es')?.split(',')[0].replace(/\//g, '-')
                                        })
                                    }}/>
                                </div>

                                <div className={filterStyles.stack}>
                                    <TextInput name={'Serie'} placeholder={'Serie'} internalName={'sn'} form={form} setForm={setForm} hideName={true}/>
                                    <DateInputAjonjolib placeholder={'Fecha de emisión'} onChange={(data) => {
                                        setForm({
                                            ...form,
                                            'issue_date': data.startDate.toLocaleString('es')?.split(',')[0].replace(/\//g, '-'),
                                            'issue_date_end': data.endDate.toLocaleString('es')?.split(',')[0].replace(/\//g, '-')
                                        })
                                    }}/>
                                </div>

                                <div className={filterStyles.stack}>
                                    <TextInput name={'Número'} placeholder={'Número'} internalName={'sn_number'} form={form} setForm={setForm} hideName={true}/>
                                    <SelectInput greyBackground={true} name={'eval_status'} placeholder={'Estado de evaluación'} internalName={'eval_status'} options={[{id: 0, name: 'Pendiente'}, {id: 1, name: 'Observado'}, {id: 2, name: 'Aceptado'}, {id: 3, name: 'Rechazado'}, {id: 4, name: 'Pre-aprobado'}]} form={form} setForm={setForm} hideName={true}/>
                                </div>

                                <div className={filterStyles.stack}>
                                    {userType === "cliente" ?
                                        <TextInput name={'R.U.C. Cliente'} placeholder={'R.U.C. Cliente'} internalName={'ruc'} form={form} setForm={setForm} hideName={true}/>
                                        :
                                        <TextInput name={'R.U.C. Proveedor'} placeholder={'R.U.C. Proveedor'} internalName={'provider_ruc'} form={form} setForm={setForm} hideName={true}/>
                                    }
                                    <SelectInput greyBackground={true} name={'payment_status'} placeholder={'Estado de pago'} internalName={'payment_status'} options={[{id: 0, name: 'Pendiente'}, {id: 1, name: 'Pagado'}]} form={form} setForm={setForm} hideName={true}/>
                                </div>

                                <div className={filterStyles.stack}>
                                    {
                                        userType === "cliente" ?
                                            <TextInput name={'Nombre Cliente'} placeholder={'Nombre Cliente'} internalName={'client_name'} form={form} setForm={setForm} hideName={true}/>
                                            :
                                            <TextInput name={'Nombre Proveedor'} placeholder={'Nombre Proveedor'} internalName={'provider_name'} form={form} setForm={setForm} hideName={true}/>
                                    }
                                    <SelectInput greyBackground={true} name={'currency'} placeholder={'Moneda'} internalName={'currency'} options={[{id: 1, name: 'Soles'}, {id: 2, name: 'Dólares'}, {id: 3, name: "Euros"}]} form={form} setForm={setForm} hideName={true}/>
                                </div>
                            </div>

                            <div className={filterStyles.filterSearchRow}>
                                <div className={filterStyles.filterSearchRowInput}>
                                    <TextInput
                                        name={'Datos ítems / JSON'}
                                        placeholder={'Buscar texto en detalle del comprobante (ítems, códigos, etc.)'}
                                        internalName={'extra_data_q'}
                                        form={form}
                                        setForm={setForm}
                                        hideName={true}
                                    />
                                </div>
                                <div className={filterStyles.filterSearchRowBtn}>
                                    <PrimaryButton variant="quiet" name={'CONSULTAR'} onClick={() => {
                                        setCurrentPage(1);
                                        query(1);
                                    }}/>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={filterStyles.secondaryShell}>
                    <div className={'d-flex flex-row justify-content-between align-items-center flex-wrap gap-1'}>
                        <div className={'d-flex flex-row opacity-0 align-items-center flex-wrap gap-1'}>
                            <div>Opciones de listado:</div>
                            <div className={filterStyles.excelBtn}>Excel</div>
                        </div>
                        <div className={'d-flex align-items-center flex-wrap'}>
                            <div className={'pe-2'}>Buscar:</div>
                            <input className={filterStyles.inlineSearchInput} value={secondaryQuery} onChange={(event) => {
                                setSecondaryQuery(event.target.value);
                            }}/>
                        </div>
                    </div>
                </div>

                <div className={'mt-3 pb-5'}>
                    {
                        //Pagination HERE
                        rows.length === 0 ? (loaded ?
                                    <div>No se encontraron resultados</div>:
                            <Loading/>
                            ) :
                            <React.Fragment>
                                <TableComponent
                                    withIndex={true}
                                    currentPage={currentPage}
                                    pageSize={25}
                                    pull={setRows}
                                    cols={cols}
                                    rows={filteredRows}
                                    fetchUrl={'bill'}
                                    rowHover
                                    consultationChrome
                                    suppressFetch
                                />
                                <Pagination currentPage={currentPage} count={pageCount} arrows={true} className={'mt-3'} onInput={(page) => {
                                    setCurrentPage(page);
                                    query(page);
                                }}/>
                            </React.Fragment>
                    }
                </div>
            </div>

            <AuditHistoryModal
                show={auditHistory.show}
                onHide={() => setAuditHistory((s) => ({ ...s, show: false }))}
                mode={auditHistory.mode}
                entityId={auditHistory.entityId}
                titleLabel={auditHistory.titleLabel}
            />
        </div>
    );
}
